import {MediaSortBy} from "@/api";
import {useDebounceState, usePaginatedQuery} from "@/hooks";
import {useDomainMediaQuery} from "@/queries.generated";
import {
    FormattedButton,
    FormattedDialogTitle,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    GenericPaginator,
    HeaderWithOrdering,
    MultipleLineError,
    PendingWrapper,
} from "@dvrt/graphql-widgets";
import {
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
} from "@mui/material";
import {useCallback, useEffect, useState} from "react";
import ThemedFontAwesomeIcon from "../ThemedFontAwesomeIcon";
import {FormattedMessage, useIntl, defineMessage} from "react-intl";
import {
    AddMediaMutation,
    AddMediaMutationVariables,
    UpdateMediaMutation,
    UpdateMediaMutationVariables,
    useAddMediaMutation,
    useDeleteMediaMutation,
    useUpdateMediaMutation,
} from "./mutations.generated";
import {useDispatch} from "react-redux";
import {showNotification} from "@/actions";
import {Form} from "@/utils/Form";
import ErrorHelper from "@/utils/ErrorHelper";
import FileSelector from "../FileSelector";
import {removeFileExtension, saveAudio} from "@/tools";
import {useAuth} from "@/context/AuthProvider";
import MediaPlayer from "../MediaPlayer";
import {msg_add, msg_delete, msg_update} from "@/utils/translations";

// Some utility types and functions to preserve compatibility with current sorting schema
type DescriptionOrdering = "description" | "~description";

const ORDERING_MAP: {[K in DescriptionOrdering]: MediaSortBy} = {
    description: MediaSortBy.DescriptionAsc,
    "~description": MediaSortBy.DescriptionDesc,
} as const;

const descriptionOrderingToMediaSortBy = (order: DescriptionOrdering[]): MediaSortBy[] => {
    return order.map((o) => ORDERING_MAP[o]);
};

export function useSortingWithCompatibility(initialOrder: DescriptionOrdering[]) {
    const [order, setOrder] = useState<MediaSortBy[]>(descriptionOrderingToMediaSortBy(initialOrder));
    const [genericOrder, setGenericOrder] = useState<DescriptionOrdering[]>(initialOrder);

    useEffect(() => {
        setOrder(descriptionOrderingToMediaSortBy(genericOrder));
    }, [genericOrder]);

    const setGenericOrderWithoutNeutral = useCallback((newOrder: string[]) => {
        if (newOrder.length === 0) {
            setGenericOrder(["description"]);
        } else {
            setGenericOrder(newOrder as DescriptionOrdering[]);
        }
    }, []);

    return {gqlOrder: order, order: genericOrder, setOrder: setGenericOrderWithoutNeutral};
}

type DeleteDialogProps = {
    dialogInfo: DialogInfo;
    onClose: () => void;
    onDelete: () => void;
};

const DeleteDialog = ({dialogInfo, onClose, onDelete}: DeleteDialogProps) => {
    if (!dialogInfo || dialogInfo.type !== "delete") return null;

    const dispatch = useDispatch();
    const [deleting, setDeleting] = useState(false);
    const [deleteMediaMutation] = useDeleteMediaMutation();

    const handleMediaDelete = async () => {
        setDeleting(true);
        await deleteMediaMutation({
            variables: {
                id: dialogInfo.mediaId,
            },
        });
        setDeleting(false);
        dispatch(
            showNotification(
                defineMessage({id: "domainMedia.dialog.deleted", defaultMessage: "Media deleted successfully"}),
                "success",
            ),
        );
        onDelete();
        onClose();
    };

    return (
        <Dialog open>
            <FormattedDialogTitle
                title={defineMessage({id: "domainMedia.dialog.delete.title", defaultMessage: "Delete media"})}
            />
            <DialogContent>
                <FormattedMessage
                    id="domainMedia.dialog.delete.content"
                    defaultMessage="Do you really want to delete the following media: {description}?"
                    values={{description: dialogInfo?.description}}
                />
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    mainButtonText={msg_delete}
                    mainAction={handleMediaDelete}
                    secondaryAction={undefined}
                    secondaryButtonHidden
                    cancel={onClose}
                    pending={deleting}
                />
            </DialogActions>
        </Dialog>
    );
};

type UpdateDialogProps = {
    dialogInfo: DialogInfo;
    onClose: () => void;
    onUpdate: () => void;
};

class UpdateForm extends Form<UpdateMediaMutation, UpdateMediaMutationVariables> {
    constructor(initialData: UpdateMediaMutationVariables) {
        super("updateMedia");
        this.hooks(useUpdateMediaMutation, initialData);
    }

    override submit(): Promise<boolean> {
        return super.submit({
            evictCaches: [
                {
                    fieldName: "media",
                    // TODO: maybe domainId as well
                },
            ],
        });
    }
}

const UpdateDialog = ({dialogInfo, onClose, onUpdate}: UpdateDialogProps) => {
    if (!dialogInfo || dialogInfo.type !== "update") return null;

    const dispatch = useDispatch();
    const [submitting, setSubmitting] = useState(false);

    const form = new UpdateForm({
        id: dialogInfo.mediaId,
        description: dialogInfo.description,
        file: undefined,
    });

    const errorHelper = new ErrorHelper(form.errors);

    const submit = async () => {
        setSubmitting(true);
        if (await form.submit()) {
            dispatch(
                showNotification(
                    defineMessage({id: "domainMedia.dialog.updated", defaultMessage: "Media updated successfully"}),
                    "success",
                ),
            );
            onUpdate();
            onClose();
        }
        setSubmitting(false);
    };

    const descriptionErrors = errorHelper.fieldErrors("description");

    return (
        <Dialog open>
            <FormattedDialogTitle
                title={defineMessage({id: "domainMedia.dialog.update.title", defaultMessage: "Update media"})}
            />
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainMedia.dialog.update.description",
                            defaultMessage: "Description",
                        })}
                    >
                        <TextField
                            value={form.values.description}
                            onChange={(event) => form.updateValues({description: event.target.value})}
                            error={descriptionErrors?.length > 0}
                            helperText={<MultipleLineError errors={descriptionErrors} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "audio",
                            defaultMessage: "Audio",
                        })}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <FileSelector
                                acceptFileType="audio/*"
                                errors={errorHelper.fieldErrors("file")}
                                acceptMultiple={false}
                                onChange={(file) => form.updateValues({file})}
                            >
                                {form.values.file?.name ? (
                                    removeFileExtension(form.values.file?.name)
                                ) : (
                                    <FormattedMessage id="upload" defaultMessage="Upload" />
                                )}
                            </FileSelector>
                            {!!form.values.file && (
                                <IconButton
                                    onClick={() => {
                                        form.updateValues({file: undefined});
                                    }}
                                >
                                    <ThemedFontAwesomeIcon icon="times" />
                                </IconButton>
                            )}
                        </Stack>
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    mainButtonText={msg_update}
                    mainAction={submit}
                    mainButtonEnabled={form.hasChanged}
                    secondaryAction={() => form.reset()}
                    secondaryButtonEnabled={form.hasChanged}
                    cancel={onClose}
                    pending={submitting}
                />
            </DialogActions>
        </Dialog>
    );
};

class AddForm extends Form<AddMediaMutation, AddMediaMutationVariables> {
    constructor(initialData: AddMediaMutationVariables) {
        super("addMedia");
        this.hooks(useAddMediaMutation, initialData);
    }

    override submit(): Promise<boolean> {
        return super.submit({
            evictCaches: [
                {
                    fieldName: "media",
                },
            ],
        });
    }
}

type AddDialogProps = {
    open: boolean;
    domainId: number;
    onClose: () => void;
    onAdd: () => void;
};

const AddDialog = ({open, domainId, onClose, onAdd}: AddDialogProps) => {
    if (!open) return null;

    const dispatch = useDispatch();
    const [submitting, setSubmitting] = useState(false);

    const form = new AddForm({
        domainId,
        description: "",
        file: null,
    });

    const errorHelper = new ErrorHelper(form.errors);

    const submit = async () => {
        setSubmitting(true);
        if (await form.submit()) {
            dispatch(
                showNotification(
                    defineMessage({id: "domainMedia.dialog.added", defaultMessage: "Media added successfully"}),
                    "success",
                ),
            );
            onAdd();
            onClose();
        }
        setSubmitting(false);
    };

    return (
        <Dialog open>
            <FormattedDialogTitle
                title={defineMessage({id: "domainMedia.dialog.add.title", defaultMessage: "Add media"})}
            />
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainMedia.dialog.add.description",
                            defaultMessage: "Description",
                        })}
                    >
                        <TextField
                            value={form.values.description}
                            onChange={(event) => form.updateValues({description: event.target.value})}
                            error={errorHelper.fieldErrors("description")?.length > 0}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("description")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "audio",
                            defaultMessage: "Audio",
                        })}
                    >
                        <FileSelector
                            acceptFileType="audio/*"
                            errors={errorHelper.fieldErrors("file")}
                            acceptMultiple={false}
                            onChange={(file) => form.updateValues({file})}
                        >
                            {form.values.file?.name ? (
                                removeFileExtension(form.values.file?.name)
                            ) : (
                                <FormattedMessage id="upload" defaultMessage="Upload" />
                            )}
                        </FileSelector>
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    mainButtonText={msg_add}
                    mainAction={submit}
                    mainButtonEnabled={form.values.description !== "" && form.values.file !== null}
                    secondaryAction={undefined}
                    secondaryButtonHidden
                    cancel={onClose}
                    pending={submitting}
                />
            </DialogActions>
        </Dialog>
    );
};

type DialogInfo =
    | {type: "add"}
    | {
          mediaId: number;
          description: string;
          type: "delete" | "update";
      };

const DownloadMediaButton = ({
    downloadToken,
    description,
}: {
    downloadToken: string | undefined;
    description: string;
}) => {
    if (!downloadToken) return null;

    const dispatch = useDispatch();
    const {accessToken} = useAuth();
    const [downloading, setDownloading] = useState(false);

    const handleDownload = useCallback(async () => {
        setDownloading(true);

        await saveAudio(downloadToken, description, accessToken, dispatch);

        setDownloading(false);
    }, [accessToken, description, dispatch, downloadToken]);

    return (
        <PendingWrapper pending={downloading}>
            <IconButton onClick={handleDownload}>
                <ThemedFontAwesomeIcon icon="download" />
            </IconButton>
        </PendingWrapper>
    );
};

type DomainMediaListProps = {
    domainId: number;
};

const DomainMediaList = ({domainId}: DomainMediaListProps) => {
    const intl = useIntl();
    const [descriptionFilter, descriptionFilterValue, setDescriptionFilter] = useDebounceState("", 400);
    const [dialogInfo, setDialogInfo] = useState<DialogInfo>(undefined);
    const {gqlOrder, order, setOrder} = useSortingWithCompatibility(["description"]);
    const mediaQuery = usePaginatedQuery(
        useDomainMediaQuery,
        {
            variables: {
                domainId,
                description: descriptionFilterValue,
                sortBy: gqlOrder,
            },
            fetchPolicy: "no-cache",
        },
        "media",
    );

    return (
        <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={2}>
                <TextField
                    variant="standard"
                    value={descriptionFilter}
                    onChange={(event) => setDescriptionFilter(event.target.value)}
                    sx={{width: "100%"}}
                    placeholder={intl.formatMessage({
                        id: "domainMedia.search.placeholder",
                        defaultMessage: "Search by description",
                    })}
                />
                <>
                    <FormattedButton
                        message={defineMessage({id: "add", defaultMessage: "Add"})}
                        onClick={() => {
                            setDialogInfo({type: "add"});
                        }}
                    />
                    <AddDialog
                        domainId={domainId}
                        onClose={() => setDialogInfo(undefined)}
                        onAdd={() => mediaQuery.query.refetch()}
                        open={dialogInfo?.type === "add"}
                    />
                </>
            </Stack>
            <TableContainer>
                <Table>
                    <HeaderWithOrdering
                        fields={[
                            {
                                path: "description",
                                label: defineMessage({
                                    id: "domainMedia.headers.description",
                                    defaultMessage: "Description",
                                }),
                            },
                            null,
                        ]}
                        multipleOrdering={false}
                        order={order}
                        orderableFields={["description"]}
                        setOrder={setOrder}
                    />
                    <TableBody>
                        {mediaQuery.query.data?.media?.items.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.description}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={2} justifyContent="end">
                                        <DownloadMediaButton
                                            downloadToken={item.downloadToken}
                                            description={item.description}
                                        />
                                        <IconButton
                                            onClick={() =>
                                                setDialogInfo({
                                                    mediaId: item.id,
                                                    description: item.description,
                                                    type: "update",
                                                })
                                            }
                                        >
                                            <ThemedFontAwesomeIcon icon="pencil" />
                                        </IconButton>
                                        <IconButton
                                            onClick={() =>
                                                setDialogInfo({
                                                    mediaId: item.id,
                                                    description: item.description,
                                                    type: "delete",
                                                })
                                            }
                                        >
                                            <ThemedFontAwesomeIcon color="error" icon="trash" />
                                        </IconButton>
                                        <MediaPlayer token={item.downloadToken} />
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <div style={{display: "flex", justifyContent: "end"}}>
                    <GenericPaginator
                        setPage={mediaQuery.setPage}
                        setRowsPerPage={mediaQuery.setPageSize}
                        page={mediaQuery.page}
                        rowsPerPage={mediaQuery.pageSize}
                        totalCount={mediaQuery.totalCount}
                    />
                </div>
            </TableContainer>
            <DeleteDialog
                dialogInfo={dialogInfo}
                onClose={() => setDialogInfo(undefined)}
                onDelete={() => mediaQuery.query.refetch()}
            />
            <UpdateDialog
                dialogInfo={dialogInfo}
                onClose={() => setDialogInfo(undefined)}
                onUpdate={() => mediaQuery.query.refetch()}
            />
        </Stack>
    );
};

export default DomainMediaList;
